import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ApiError, fetchPhotoObjectUrl } from '../api';
import { ar } from '../ar';
import { IconAlert, IconClock, IconExpand } from './icons';

/**
 * Photo evidence — the hero of the report.
 *
 * The functional rule is absolute and unchanged from the original build: no
 * `aspect-ratio`, no `object-fit: cover`, no fixed 4:3 / 1:1 / 16:9 anywhere in this
 * chain. A portrait capture stays portrait, a landscape capture stays landscape, on
 * screen, in the lightbox and in print. Cropping evidence would defeat the product.
 */

export function EvidenceSection({
  title,
  count,
  icon,
  children,
}: {
  title: string;
  count: number;
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="evidence-section" aria-label={title}>
      <div className="evidence-head">
        <span className="evidence-icon" aria-hidden="true">
          {icon}
        </span>
        <h3 className="evidence-title">{title}</h3>
        <span className="evidence-rule" aria-hidden="true" />
        <span className="evidence-count num">{ar.photoCount(count)}</span>
      </div>
      <div className="evidence-grid">{children}</div>
    </section>
  );
}

export function EvidencePhoto({
  token,
  photoId,
  alt,
  sequence,
  timeLabel,
  clockAdjusted,
  onReady,
  onOpen,
}: {
  token: string;
  photoId: string;
  alt: string;
  sequence: number;
  timeLabel: string;
  clockAdjusted?: boolean;
  onReady?: (photoId: string, url: string | null) => void;
  onOpen?: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const readyRef = useRef(onReady);
  readyRef.current = onReady;

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    void (async () => {
      try {
        // R2 is private and an <img src> cannot carry an Authorization header, so the
        // bytes are fetched with the bearer token and handed over as an object URL.
        // No credential ever appears in a URL.
        objectUrl = await fetchPhotoObjectUrl(token, photoId);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setUrl(objectUrl);
      } catch (error) {
        if (cancelled) return;
        setFailed(true);
        readyRef.current?.(photoId, null);
        if (!(error instanceof ApiError)) throw error;
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [token, photoId]);

  if (failed) {
    return (
      <figure className="photo-card photo-card-failed">
        <div className="photo-failed" role="img" aria-label={ar.imageFailed}>
          <IconAlert size={17} />
          <span>{ar.imageFailed}</span>
        </div>
        <figcaption className="photo-caption">
          <span className="photo-seq num" dir="ltr">
            {sequence}
          </span>
        </figcaption>
      </figure>
    );
  }

  return (
    <figure className="photo-card">
      <button
        className="photo-frame"
        onClick={onOpen}
        aria-label={`${alt} — ${ar.openPhoto}`}
        disabled={!url}
      >
        {url ? (
          <img
            className="evidence-photo"
            src={url}
            alt={alt}
            // Decoded synchronously: print snapshots the page as it is, and an
            // undecoded image prints as a blank panel.
            decoding="sync"
            loading="eager"
            onLoad={() => readyRef.current?.(photoId, url)}
            onError={() => {
              setFailed(true);
              readyRef.current?.(photoId, null);
            }}
          />
        ) : (
          <span className="photo-placeholder skeleton" aria-hidden="true" />
        )}
        <span className="photo-expand no-print" aria-hidden="true">
          <IconExpand size={14} />
        </span>
      </button>

      <figcaption className="photo-caption">
        <span className="photo-seq num" dir="ltr">
          {sequence}
        </span>
        <span className="photo-time num">
          <IconClock size={11} />
          {timeLabel}
        </span>
        {clockAdjusted && <span className="photo-flag">{ar.clockAdjusted}</span>}
      </figcaption>
    </figure>
  );
}
